import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db, isDemoMode } from "../firebase";
import { findDemoProfileByPhone } from "../demoData";
import { saveRolodexEntry } from "../lib/rolodexStore";
import { loadPublishedProfile } from "../lib/profileStore";
import {
  contextsFromSelection,
  useRelationshipDraft,
} from "../hooks/useRelationshipEditing";
import {
  contextGateReason,
  contextsAreAllowed,
  isUsablePhone,
  normalizePhone,
} from "../lib/connectionEligibility";
import ContextSelector from "./ContextSelector";
import PrivacyToggle from "./PrivacyToggle";
import ContextBadges from "./ContextBadges";
import FacetEditor from "./FacetEditor";
import PrivateContactFields, {
  EMPTY_PRIVATE_CONTACT_PROFILE,
} from "./PrivateContactFields";

const LOOKUP_DEBOUNCE_MS = 450;

/**
 * Add a connection in one screen.
 *
 * A name is all that is required. The phone number is the trust gate, so it is
 * only mandatory for a professional facet — and when it is supplied, the Aline
 * profile lookup happens inline rather than as its own step.
 */
export default function AddConnectionModal({
  currentUser,
  onClose,
  networkNodes,
  entries = [],
  initialContext = "professional",
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [lookupDone, setLookupDone] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [privateProfile, setPrivateProfile] = useState({ ...EMPTY_PRIVATE_CONTACT_PROFILE });
  const [lookupError, setLookupError] = useState("");
  const [saved, setSaved] = useState(null);

  const validInitialContext = ["professional", "personal", "both"].includes(initialContext)
    ? initialContext
    : "professional";
  const relationship = useRelationshipDraft({
    entries,
    initialContexts: contextsFromSelection(validInitialContext),
    initialActiveContext: initialContext === "personal" ? "personal" : "professional",
    sourceKey: "new",
    liveHydration: false,
  });

  const existingEntry = useMemo(() => {
    const normalized = normalizePhone(phone);
    if (!foundUser?.id && !isUsablePhone(phone)) return null;
    return (
      entries.find((entry) =>
        foundUser?.id
          ? entry.recipient_uid === foundUser.id
          : entry.recipient_phone === normalized
      ) || null
    );
  }, [entries, foundUser, phone]);

  const draft = { recipient_phone: phone, recipient_uid: foundUser?.id || null };
  const requestedContexts = relationship.enabledContexts || [];
  const gateReason = requestedContexts
    .map((context) => contextGateReason(context, draft))
    .find(Boolean);
  const contextsAllowed = contextsAreAllowed(requestedContexts, draft);
  const error = lookupError || relationship.submissionError;

  // Look the number up as it is typed; nothing about the form waits on it.
  useEffect(() => {
    const normalized = normalizePhone(phone);
    if (!isUsablePhone(phone)) {
      setFoundUser(null);
      setLookupDone(false);
      return undefined;
    }

    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        let userProfile = null;
        if (isDemoMode) {
          userProfile = findDemoProfileByPhone(normalized);
        } else {
          const indexDoc = await getDoc(doc(db, "phone_index", normalized));
          if (indexDoc.exists()) {
            const { uid } = indexDoc.data();
            userProfile =
              (await loadPublishedProfile(uid)) || { id: uid, uid, full_name: "Aline user" };
          }
        }
        if (cancelled) return;
        setFoundUser(userProfile);
        setLookupDone(true);
        setLookupError("");
        if (userProfile?.full_name) setName((current) => current || userProfile.full_name);
      } catch (_) {
        if (!cancelled) setLookupError("That phone number could not be looked up.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phone]);

  const handleSave = async () => {
    setLookupError("");
    let newEntryId = null;
    const succeeded = await relationship.submit(
      async () => {
        newEntryId = await saveRolodexEntry(currentUser.uid, {
          ...(existingEntry || {}),
          recipient_uid: foundUser?.id || existingEntry?.recipient_uid || null,
          recipient_phone: normalizePhone(phone),
          recipient_name: name.trim() || foundUser?.full_name || "",
          status: "confirmed",
          ...(!foundUser ? { private_profile: privateProfile } : {}),
          facets: relationship.storedFacets(entries),
        });
      },
      {
        errorMessage: (nextError) =>
          nextError?.code === "rolodex/duplicate-recipient"
            ? "This person is already in your rolodex. Open their profile to add another context."
            : "The connection could not be saved. Try again.",
      }
    );
    // Demo mode saves nothing and returns no id; the receipt still shows.
    if (succeeded) setSaved(newEntryId || true);
  };

  // Saving lands you on the new profile, which is where enrichment happens.
  const finish = () => {
    onClose();
    if (typeof saved === "string") navigate(`/connections/${saved}`);
  };

  const canSave = Boolean(name.trim()) && contextsAllowed && !relationship.isSubmitting;

  if (saved) {
    return (
      <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && finish()}>
        <div className="modal-card context-modal-card">
          <div className="modal-body context-confirmation">
            <div className="context-confirmation-mark" aria-hidden="true">✓</div>
            <p><strong>{name.trim()}</strong> is now in your rolodex.</p>
            <ContextBadges contexts={relationship.enabledContexts} />
            <p className="modal-helper-copy">
              Open their profile to add a photo, moments you shared, and how you know them.
            </p>
          </div>
          <div className="modal-footer">
            <button className="btn-primary btn-sm" onClick={finish}>
              {typeof saved === "string" ? "Open their profile" : "Done"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal-card context-modal-card">
        <div className="modal-header">
          <div>
            <p className="eyebrow">Add someone to your rolodex</p>
            <h3 className="modal-title">Who are they?</h3>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body context-modal-body">
          {error && <div className="error-banner">{error}</div>}

          <div className="form-field">
            <label htmlFor="add-connection-name">Full name</label>
            <input
              id="add-connection-name"
              type="text"
              placeholder="Amara Okafor"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </div>

          <ContextSelector
            value={relationship.contextSelection}
            onChange={(selection) => relationship.selectContexts(contextsFromSelection(selection))}
            disabled={relationship.isSubmitting}
          />

          {requestedContexts.includes("professional") && (
            <div className="form-field">
              <label htmlFor="add-connection-phone">
                Phone number <span className="form-field-required">required for professional</span>
              </label>
              <input
                id="add-connection-phone"
                type="tel"
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
              <p className="form-field-hint">
                {searching
                  ? "Checking for an Aline profile…"
                  : "Aline uses the number to confirm who they are — it is never shown to them."}
              </p>
            </div>
          )}

          {!requestedContexts.includes("professional") && (
            <div className="form-field">
              <label htmlFor="add-connection-phone-optional">Phone number (optional)</label>
              <input
                id="add-connection-phone-optional"
                type="tel"
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          )}

          {/* Only explain the gate once they are actually filling the form in. */}
          {/* Discovery consent belongs on the main screen, not behind a
              disclosure: it decides whether this person can ever be found
              through your network, and professional defaults to on. */}
          {requestedContexts.map((context) => (
            <PrivacyToggle
              key={context}
              id={`add-${context}-discovery`}
              label={
                requestedContexts.length > 1
                  ? `Include in extended network — ${context}`
                  : "Include in extended network"
              }
              description={
                context === "personal"
                  ? "Off by default. Turn it on to let this person appear in introduction paths."
                  : "Let this person appear in extended-network search and introduction paths."
              }
              value={relationship.facets?.[context]?.discoveryEnabled === true}
              onChange={(discoveryEnabled) =>
                relationship.setFacets((previous) => ({
                  ...previous,
                  [context]: { ...(previous?.[context] || {}), discoveryEnabled },
                }))
              }
              disabled={relationship.isSubmitting}
            />
          ))}

          {gateReason && name.trim() && (
            <div className="context-existing-notice">{gateReason}</div>
          )}

          {foundUser && (
            <div className="phone-result">
              <div className="phone-result-avatar">
                {foundUser.full_name?.split(" ").map((part) => part[0]).join("").slice(0, 2)}
              </div>
              <div className="phone-result-copy">
                <strong>{foundUser.full_name}</strong>
                <span>{foundUser.school || foundUser.role || "Aline user"}</span>
              </div>
            </div>
          )}

          {lookupDone && !foundUser && (
            <div className="phone-result-not-found">
              No Aline profile on that number — they will live in your private rolodex only.
            </div>
          )}

          {existingEntry && (
            <div className="context-existing-notice">
              They are already in your rolodex. Saving will add or update the selected side without
              changing the other one.
            </div>
          )}

          <button
            type="button"
            className="detail-list-add"
            onClick={() => setShowDetails((open) => !open)}
          >
            {showDetails ? "Hide extra details" : "+ Add more details now"}
          </button>

          {showDetails && (
            <>
              {!foundUser && (
                <PrivateContactFields
                  idPrefix="add-private-contact"
                  name={name}
                  onNameChange={setName}
                  value={privateProfile}
                  onChange={setPrivateProfile}
                  disabled={relationship.isSubmitting}
                />
              )}
              <FacetEditor
                relationship={relationship}
                networkNodes={networkNodes}
                currentUid={currentUser?.uid}
              />
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" onClick={handleSave} disabled={!canSave}>
            {relationship.isSubmitting ? "Saving…" : "Save connection"}
          </button>
        </div>
      </div>
    </div>
  );
}
